import { Component, EventEmitter, HostListener, Input, Output, ViewEncapsulation } from '@angular/core';

/**
 * QuizLayoutComponent — wrapper visivo condiviso da tutte le pagine del flusso
 * compilazione (homepage, intestazione, domande, conclusione, nopermissions,
 * nocompleted). Garantisce header/wrapper/footer coerenti, design system
 * (colori `#C8273D`/`#661615`, font Poppins, radius 20, ombre morbide) e
 * comportamento responsive desktop+mobile.
 *
 * Pattern: dumb component. Logica/stato vive nelle pagine; questo componente
 * espone solo @Input visivi e un @Output `backClick` opzionale.
 *
 * Usage:
 * ```html
 * <app-quiz-layout
 *   [title]="questTitle"
 *   [custLogo]="compile.custLogo"
 *   [variant]="'wide'"
 *   [showProgress]="true"
 *   [progressValue]="42"
 *   [showBack]="qIndex > 0"
 *   (backClick)="onBack()">
 *   <ng-content>... contenuto pagina ...</ng-content>
 * </app-quiz-layout>
 * ```
 */
@Component({
  selector: 'app-quiz-layout',
  templateUrl: './quiz-layout.component.html',
  styleUrls: ['./quiz-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class QuizLayoutComponent {
  /** Titolo mostrato nell'header (es. titolo questionario). */
  @Input() title = '';

  /** Logo cliente custom (sostituisce/affianca il logo Amajor). */
  @Input() custLogo: any = null;

  /** Variante larghezza wrapper. `narrow` 720px (homepage/landing), `wide` 1080px (compilazione). */
  @Input() variant: 'narrow' | 'wide' = 'wide';

  /** Mostra la progress bar nell'header. */
  @Input() showProgress = false;

  /** Valore progress 0-100. */
  @Input() progressValue = 0;

  /** Mostra il pulsante "indietro" nell'header sinistro. */
  @Input() showBack = false;

  /** Mostra il pulsante "avanti" nell'header sinistro, a fianco del back. */
  @Input() showForward = false;

  /** Disabilita transitions/animations (rispetta `prefers-reduced-motion`). */
  @Input() reducedMotion = false;

  /** Variante "centered" per pagine messaggio (nopermissions/nocompleted). */
  @Input() centered = false;

  /** Lingue disponibili per il questionario (es. ['ITA', 'ENG']). Se length <= 1 il selettore è nascosto. */
  @Input() availableLanguages: string[] = [];

  /** Lingua attualmente selezionata (es. 'ITA' | 'ENG'). */
  @Input() currentLanguage: string = 'ITA';

  @Output() backClick = new EventEmitter<void>();

  /** Emesso quando l'utente clicca il pulsante "avanti" nell'header. */
  @Output() forwardClick = new EventEmitter<void>();

  /** Emesso quando l'utente cambia lingua dal selettore nel topbar. */
  @Output() languageChange = new EventEmitter<string>();

  /** Stato aperto/chiuso del dropdown lingua nel topbar. */
  langMenuOpen = false;

  toggleLangMenu(event: Event): void {
    event.stopPropagation();
    this.langMenuOpen = !this.langMenuOpen;
  }

  onLanguageSelect(lang: string, event: Event): void {
    event.stopPropagation();
    this.langMenuOpen = false;
    if (lang === this.currentLanguage) return;
    this.languageChange.emit(lang);
  }

  /** Etichetta leggibile per la lingua corrente (es. ITA → Italiano). */
  getLanguageLabel(lang: string): string {
    if (lang === 'ITA') return 'Italiano';
    if (lang === 'ENG') return 'English';
    return lang;
  }

  /**
   * Chiude il dropdown lingua se l'utente clicca fuori dal trigger/menu.
   * Usa `target.closest()` invece di stopPropagation per essere robusto
   * rispetto a timing di bubbling e capture phase.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.langMenuOpen) return;
    const target = event.target as HTMLElement | null;
    if (target && target.closest && target.closest('.quiz-shell__lang')) return;
    this.langMenuOpen = false;
  }

  /** Toggle hide-on-scroll della top bar (mobile). Il footer sticky resta sempre visibile. */
  hideTopBar = false;
  private lastScrollY = 0;

  onBack(): void {
    this.backClick.emit();
  }

  onForward(): void {
    this.forwardClick.emit();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    const delta = y - this.lastScrollY;

    if (y < 80) {
      this.hideTopBar = false;
    } else if (delta > 8) {
      this.hideTopBar = true;
    } else if (delta < -8) {
      this.hideTopBar = false;
    }

    this.lastScrollY = y;
  }
}
